import axios from 'axios'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import duration from 'dayjs/plugin/duration'
import { getUiServicesEndpoint } from './utils'
import { logEvent, LogLevel } from '@praxis/component-logging'
import { isEmpty, mapKeys, camelCase } from 'lodash'
import { purchaseOrderKey } from 'common/utils'
import { DateInterface } from 'components/TimeBeforeRelease'

export const getExpectedReleaseDays = (
  expectedReleaseDate: Date,
): DateInterface => {
  if (!expectedReleaseDate) {
    return { days: '—' }
  }
  dayjs.extend(utc)
  dayjs.extend(timezone)
  dayjs.extend(duration)
  const now = dayjs().tz('America/Chicago')
  const end = dayjs(expectedReleaseDate).tz('America/Chicago')
  const dayDifference = end.diff(now, 'day')
  if (dayDifference !== 0) {
    return { days: dayDifference }
  }
  const hourDifference = end.diff(now, 'hour')
  if (hourDifference < 0) {
    return { days: -1 }
  } else {
    return {
      days: dayDifference,
      hours: hourDifference,
    }
  }
}

export const transformPaginationMetadata = (data: {
  total_count?: number
  current_page?: number
  total_pages?: number
}) => {
  if (isEmpty(data)) {
    return {}
  }

  return {
    totalCount: data.total_count,
    currentPage: data.current_page,
    totalPages: data.total_pages,
  }
}

export const transformPurchaseOrders = (data: any) => {
  if (!Array.isArray(data)) {
    return []
  }
  return data.reduce((prev: any, curr: any) => {
    const {
      expected_release_ts,
      department,
      purchase_order_number,
      total_cartons,
      total_cubes,
      total_pallets,
      total_weight,
      total_shipments,
      shipments_pending_review,
      vendor,
      cancelled,
      manual_shipment_allowed,
    } = curr

    const { id: vendor_id, name, origin_location = {} } = vendor

    const {
      city = '',
      country = '',
      logistics_group = '',
      state = '',
      street = '',
      time_zone = '',
      tm_location_id = '',
      vmm_location_id = '',
      zipcode = '',
      business_hours = [],
      closure_dates = [],
    } = origin_location
    let businessHoursWithCamelcase = business_hours.map((el: any) =>
      mapKeys(el, (val, key) => camelCase(key)),
    )

    return {
      ...prev,
      [purchaseOrderKey({ purchaseOrder: purchase_order_number, department })]:
        {
          city,
          country,
          department,
          expectedReleaseDays: getExpectedReleaseDays(expected_release_ts),
          expectedReleaseTs: expected_release_ts,
          haveAllPOShipmentsBeenReviewed: shipments_pending_review === 0,
          isFailedVendorDetailFetch:
            typeof name !== 'string' || typeof origin_location !== 'object',
          logisticGroup: logistics_group,
          purchaseOrder: purchase_order_number,
          shipmentsPendingReview: shipments_pending_review,
          state,
          street,
          timeZone: time_zone,
          tmLocationId: tm_location_id,
          totalCartons: total_cartons,
          totalCubes: total_cubes,
          totalPallets: total_pallets,
          totalShipments: total_shipments,
          totalWeight: total_weight,
          vendorName: name,
          vmmLocationId: vmm_location_id,
          zipCode: zipcode,
          businessHours: businessHoursWithCamelcase,
          vendorId: vendor_id,
          cancelled: cancelled,
          closureDates: closure_dates,
          manualShipmentAllowed: manual_shipment_allowed,
        },
    }
  }, {})
}

export const fetchPurchaseOrder = async (
  vendorIds?: Array<string>,
  purchaseOrders?: Array<string>,
  dateFrom?: string,
  dateTo?: string,
  shipmentIds?: Array<string>,
  loadIds?: Array<string>,
  departmentIds?: Array<string>,
  tmLocIds?: Array<string>,
  bookingCategories?: Array<string>,
  truckLoadOptimizationRefIds?: Array<string>,
  currentPage = 1,
  itemsPerPage = 10,
  destinationsLocCodes?: Array<string>,
) => {
  const { key, url } = await getUiServicesEndpoint()
  const params = {
    vendor_ids: vendorIds?.join(','),
    po_numbers: purchaseOrders?.join(','),
    key,
    page: currentPage,
    per_page: itemsPerPage,
    date_from: dateFrom,
    date_to: dateTo,
    tgt_booking_ids: shipmentIds?.join(','),
    load_ids: loadIds?.join(','),
    department_ids: departmentIds?.join(','),
    origin_location_codes: tmLocIds?.join(','),
    booking_categories: bookingCategories?.join(','),
    destination_location_codes: destinationsLocCodes?.join(','),
    tlo_ref_ids: truckLoadOptimizationRefIds?.join(','),
  }
  const endpoint = `${url}/v1/search/purchase_orders`
  try {
    const response = await axios.get(endpoint, {
      params,
    })
    return response.data || []
  } catch (err: any) {
    const message = `Failed to Fetch Purchase Orders, Please refine your search criteria`
    logEvent(
      {
        message: JSON.stringify({
          endpoint,
          params,
          error: err?.response?.data,
          message: message,
          type: 'REQUEST_FAILURE',
          user: localStorage?.userInfo,
        }),
        url: window.location.href,
      },
      { level: LogLevel.Error },
    )

    throw Error(message)
  }
}
